import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { IntegrationParameterDTO, IntegrationParameterFilterDTO, IntegrationSettingDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, DatePickers } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId } from '../../../shared/environment';
import { IntegrationParameterMaintenance } from './IntegrationParameterMaintenance';

interface IIntegrationParameterProps {
    data: any
}
export const IntegrationParameter: React.FC<IIntegrationParameterProps> = ({ data }) => {

    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [integrationSettingList, setIntegrationSettingList] = useState<IntegrationSettingDTO[]>([]);
    const [integrationParameterList, setIntegrationParameterList] = useState<IntegrationParameterDTO[]>([]);
    const [integrationParameterFilterDTO, setIntegrationParameterFilterDTO] = useState<IntegrationParameterFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setIntegrationParameterFilterDTO({
            ...integrationParameterFilterDTO,
            id: '',
            typeId: '',
            integrationSettingId: data.id ?? '',
        } as IntegrationParameterFilterDTO);
    }, []);

    useEffect(() => {
        handleListGetAll(ListId.linksphere.integrationParameter.typeId, setTypeList);
        handleIntegrationSettingGetAll();
        handleIntegrationParameterGetAllViewAsync();
    }, [integrationParameterFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleIntegrationParameterGetAllViewAsync();
    }, [actionExecuted]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleIntegrationSettingGetAll = useCallback(async () => {
        const integrationSettingFilterDTO = {

        };
        GenericService.GetAllAsync1('linksphere.integrationSetting', integrationSettingFilterDTO, setLoading, setIntegrationSettingList, showMessage);
    }, []);

    const handleIntegrationParameterGetAllViewAsync = useCallback(async () => {
        if (integrationParameterFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('linksphere.integrationParameter', integrationParameterFilterDTO, setActionExecuted, setLoading, setIntegrationParameterList, showMessage, true);
    }, [integrationParameterFilterDTO]);

    const handleClearFilterClick = () => {
        setIntegrationParameterFilterDTO({
            ...integrationParameterFilterDTO,
            integrationParameterSettingId: '',
            sourceId: '',
            statusId: '',
            startDateTime: undefined,
            endDateTime: undefined
        } as IntegrationParameterFilterDTO);
    };

    function handleSearchClick() {
        handleIntegrationParameterGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Integration Parameter', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: integrationParameterList.length },
        { label: 'Type', value: typeList.find(x => x.id === integrationParameterFilterDTO?.typeId)?.value },
    ];

    const columns: ICardColumns[] = [
        { field: 'type', label: 'Type', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'key', label: 'Key', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'value', label: 'Value', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'integrationSetting', label: 'Integration Setting', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='INTEGRATION PARAMETER'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='typeId'
                                label='Type'
                                setValue={setIntegrationParameterFilterDTO}
                                value={integrationParameterFilterDTO?.typeId}
                                obj={integrationParameterFilterDTO}
                                valueList={typeList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '80vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={integrationParameterList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <IntegrationParameterMaintenance
                        data={''}
                        parentId={data.id ?? ''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <IntegrationParameterMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'linksphere.integrationParameter'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};